import { useEffect, useState } from "react";
import { Check, DoorOpen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { LobbyParticipant } from "../api/types/video";

interface WaitingRoomBannerProps {
  waitingParticipants: LobbyParticipant[];
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
}

/**
 * Auto-shown top-right popup while guests are waiting to be admitted.
 * Dismissible (X) without denying anyone — the same list stays reachable via
 * the People popover's "Waiting room" section. Re-arms (un-dismisses) the
 * next time the waiting list goes from empty back to non-empty.
 */
export function WaitingRoomBanner({ waitingParticipants, onApprove, onDeny }: WaitingRoomBannerProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (waitingParticipants.length === 0) setDismissed(false);
  }, [waitingParticipants.length]);

  if (waitingParticipants.length === 0 || dismissed) return null;

  return (
    <div className="absolute top-4 end-4 z-20 w-72 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden anim-scale-in">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <DoorOpen className="size-4 text-primary" />
        <span className="text-foreground text-xs font-black uppercase tracking-widest">
          {t("call.controls.waitingRoomSection")}
        </span>
        <span className="ms-auto flex items-center gap-2">
          <span className="size-5 rounded-full bg-primary text-primary-foreground text-[11px] font-black flex items-center justify-center">
            {waitingParticipants.length}
          </span>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            title={t("call.controls.waitingRoomDismiss")}
            aria-label={t("call.controls.waitingRoomDismiss")}
            className="size-6 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="size-[15px]" />
          </button>
        </span>
      </div>
      <div className="max-h-72 overflow-y-auto no-scrollbar py-1">
        {waitingParticipants.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-3 py-3 anim-fade">
            <span className="shrink-0 size-9 rounded-full flex items-center justify-center text-sm font-bold bg-neutral-600 text-white select-none">
              {(p.username.trim()[0] ?? "?").toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-foreground text-sm font-bold truncate leading-tight">{p.username}</p>
              <p className="text-muted-foreground text-[11px] font-medium">
                {p.company ?? t("call.controls.wantsToJoin")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onDeny(p.id)}
              title={t("call.participants.reject")}
              aria-label={t("call.participants.rejectAriaLabel", { username: p.username })}
              className="size-9 rounded-xl bg-muted text-muted-foreground hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors"
            >
              <X className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => onApprove(p.id)}
              title={t("call.participants.approve")}
              aria-label={t("call.participants.approveAriaLabel", { username: p.username })}
              className="size-9 rounded-xl bg-primary text-primary-foreground hover:scale-105 flex items-center justify-center transition-transform"
            >
              <Check className="size-4" />
            </button>
          </div>
        ))}
      </div>
      {waitingParticipants.length > 1 && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
          <button
            type="button"
            onClick={() => waitingParticipants.forEach((p) => onDeny(p.id))}
            className="flex-1 h-8 rounded-xl text-muted-foreground hover:bg-muted text-xs font-bold transition-colors"
          >
            {t("call.controls.denyAll")}
          </button>
          <button
            type="button"
            onClick={() => waitingParticipants.forEach((p) => onApprove(p.id))}
            className="flex-1 h-8 rounded-xl bg-foreground/[0.06] border border-border text-foreground hover:bg-foreground/10 text-xs font-bold transition-colors"
          >
            {t("call.controls.admitAll")}
          </button>
        </div>
      )}
    </div>
  );
}
